import * as React from 'react';
import { useQuery } from 'react-query';
import { Image } from 'antd';
import styled from 'styled-components';

import { CmsReview } from '@doltech/types';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { apis } from '@doltech/core/lib/api/api.config';
import { apiPublicUrls } from '@doltech/core/lib/api/api.public-urls';

import { ResponsiveTypography } from '../../../../Typography/v3/ResponsiveTypography';
import { AnchorPicker } from './AnchorPicker';
import { ReviewItem } from './ReviewItem';
import { ReviewLayout, ReviewLayoutProps, ReviewLayoutSkeleton, WithAnchor } from './ReviewLayout';

interface CenterReviewProps
  extends React.HTMLAttributes<HTMLDivElement>,
    WithAnchor,
    Pick<ReviewLayoutProps, 'metaRight'> {
  data: CmsReview;
  onSelectAnchor?: (item) => void;
  selectedAnchor?: string;
}

const CenterHeading = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) max-content;

  ${fromScreen(768)} {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`;

interface ConnectedCenterReviewProps extends Omit<CenterReviewProps, 'data'> {
  id: string;
}

export const ConnectedCenterReview = ({ id, ...rest }: ConnectedCenterReviewProps) => {
  const { data: res, isLoading } = useQuery(['bookReview', id], () =>
    apis.cms.get<CmsReview>(apiPublicUrls.cms.CENTER_REVIEW_BY_ID, { id })
  );

  if (isLoading) return <ReviewLayoutSkeleton />;

  if (!res) return null;

  return <CenterReview data={res.data} {...rest} />;
};

export const CenterReview = ({
  data,
  selectedAnchor,
  onSelectAnchor,
  ...rest
}: CenterReviewProps) => {
  return (
    <ReviewLayout
      metaLeft={
        <Image
          src={data?.thumbnail?.url}
          preview={false}
          alt=""
          placeholder
          style={{ borderRadius: 8 }}
        />
      }
      metaRight={<AnchorPicker onChange={onSelectAnchor} defaultValue={selectedAnchor} />}
      heading={
        <CenterHeading>
          <ResponsiveTypography.Text variant="semi-bold/20">{data?.name}</ResponsiveTypography.Text>
          <ResponsiveTypography.Text variant="semi-bold/24" color="green100">
            {data?.rating}
          </ResponsiveTypography.Text>
        </CenterHeading>
      }
      reviews={[
        {
          key: 'overviewReviews',
          heading: null,
          content: (data?.overview_reviews || []).map((eachReview) => (
            <ReviewItem
              key={eachReview.id || eachReview._id}
              title={eachReview.title}
              description={eachReview.description}
              content={eachReview.value}
            />
          )),
        },

        {
          key: '2',
          heading: (
            <ResponsiveTypography.Text variant="regular/16">
              <strong>Đánh giá về khoá học</strong> (trên thang điểm 10)
            </ResponsiveTypography.Text>
          ),
          content: (data?.detail_reviews || []).map((eachReview) => (
            <ReviewItem
              key={eachReview.id || eachReview._id}
              title={eachReview.title}
              description={eachReview.description}
              content={eachReview.value}
            />
          )),
        },
      ]}
      {...rest}
    />
  );
};
